import { safeFetch } from "@/lib/api/safe-fetch";
import {
  StripeUrlResponse,
  SubscriptionStatusResponse,
} from "@ripenn/api-contracts/webapp/billing";
import type { BillingCycle, PlanType } from "@/stores/paymentSelection/types";

export async function createPortalSession(orgSlug?: string): Promise<string> {
  // Without an org the handler treats the missing body as {} (no org
  // scoping). HttpError from safeFetch carries the server's error
  // copy for the caller's toast.
  const { url } = await safeFetch(
    StripeUrlResponse,
    "/api/billing/create-portal-session",
    orgSlug
      ? { method: "POST", body: JSON.stringify({ org_slug: orgSlug }) }
      : { method: "POST" },
  );
  return url;
}

export async function createCheckoutSession(params: {
  plan: PlanType | null;
  billingCycle: BillingCycle | null;
  context?: string;
  project_id?: string;
  org_slug?: string;
}): Promise<string> {
  // Null plan / cycle (nothing selected yet) is passed through and
  // rejected server-side with a 400, matching the pre-contract
  // behavior; HttpError surfaces the server copy to the caller.
  const { url } = await safeFetch(
    StripeUrlResponse,
    "/api/billing/create-checkout-session",
    {
      method: "POST",
      body: JSON.stringify({
        plan_type: params.plan,
        billing_cycle: params.billingCycle,
        context: params.context,
        project_id: params.project_id,
        org_slug: params.org_slug,
      }),
    },
  );
  return url;
}

export async function getSubscriptionStatus(
  orgId?: string,
): Promise<SubscriptionStatusResponse | null> {
  // Swallows every failure (HttpError, ContractError, network) and
  // returns null — callers (useSubscription, useFeatureAccess) treat
  // null as the free-tier default, matching the pre-contract
  // behavior.
  try {
    return await safeFetch(
      SubscriptionStatusResponse,
      "/api/billing/subscription-status",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(orgId ? { org_id: orgId } : {}),
      },
    );
  } catch (error) {
    console.error("Error fetching subscription status:", error);
    return null;
  }
}
